/* Notification bell + feed panel (Task-Management Phase 1 / T1).
 *
 * The RENDER half of the notification stack — subscribes to lib/
 * notifications.ts's NOTIF model (the data half) and paints a bell button
 * (with an unread-count badge) plus a dropdown feed, into a host element the
 * shell provides (#notifBell in hub/index.html). Same data/render split, and
 * the same lit `render(view(), host)` idiom, that state/workspace.ts + ws/*.ts
 * use. All styling is token-only (styles/workspace.css `.notif*`) — no raw
 * hex/px here.
 *
 * Deep-link (notifBell follow-on — closes the T1 F2 gap): clicking a task-bound
 * notification marks it read and navigates to that task via T2's shared grammar
 * (ws/taskLink.ts's buildTaskHref → `?load=<run>&task=<id>#s7`, consumed by
 * openTaskDeepLink on the destination hub's boot). It is cross-client safe: the
 * feed row carries the client's server-resolved `run_file`, so a row for ANOTHER
 * client routes to THAT client's board and lands ON the task (drawer open +
 * flash). A per-client roll-up row (kind due/stall, no task_id) deep-links to
 * that client's board; a client-note-only notification (no task_id, kind
 * mention) falls back to `#notes` per the helper's contract.
 */
import { html, nothing, render, svg } from 'lit';
import { NOTIF, type Notification } from '../lib/notifications.ts';
import { buildTaskHref } from './taskLink.ts';

// bell glyph in the shared `.mi` stroke-icon family (state/workspace.ts's ICON)
const bellIcon = svg`<svg class="mi" viewBox="0 0 24 24"><path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.7 21a2 2 0 0 1-3.4 0"/></svg>`;

const KIND_LABEL: Record<string, string> = {
  assigned: 'Assigned',
  mention: 'Mention',
  due: 'Due',
  stall: 'Stalled',
  regression: 'Regressed',
};

/** Compact relative time ("just now", "5m", "3h", "2d", else a date). */
function timeAgo(iso: string): string {
  const t = Date.parse(iso);
  if (!t) return '';
  const s = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return m + 'm';
  const h = Math.floor(m / 60);
  if (h < 24) return h + 'h';
  const d = Math.floor(h / 24);
  if (d < 7) return d + 'd';
  return new Date(t).toISOString().slice(0, 10);
}

let open = false;
let host: HTMLElement | null = null;
let unsub: (() => void) | null = null;

// Kinds whose task_id-less row is a per-client ROLL-UP ("N tasks overdue in
// <brand>") → deep-links to that client's board (#s7). Any other task_id-less
// row (a client-note @mention) falls back to the notes card (#notes), matching
// the T2 helper's client-note landing contract.
const ROLLUP_BOARD_KINDS = new Set(['due', 'stall', 'regression']);

/** The deep-link href for a notification row, in T2's grammar. A task-bound row
 * routes THROUGH buildTaskHref (cross-client safe via the row's server-resolved
 * `run_file`) and lands on the task; a task_id-less roll-up lands on its
 * client's board; a task_id-less client-note lands on `#notes`. Pure, so the
 * routing is unit-testable without touching navigation. */
export function notifHref(n: Notification): string {
  const runFile = n.run_file ?? null;
  if (n.task_id != null) {
    return buildTaskHref({ clientSlug: n.client_slug, taskId: n.task_id, runFile });
  }
  const base = runFile
    ? `/review/?load=${encodeURIComponent('/test-runs/' + runFile)}`
    : '/review/?x=1';
  return `${base}${ROLLUP_BOARD_KINDS.has(n.kind) ? '#s7' : '#notes'}`;
}

// Navigation seam — a full load to the (possibly different) client's hub, where
// openTaskDeepLink (main.ts boot) consumes `?task=`/`#notes`. Injectable so the
// unit tests can assert the target without jsdom's unimplemented navigation.
let navigate: (href: string) => void = (href) => {
  window.location.assign(href);
};

/** Test seam: override the navigation sink (jsdom can't navigate). */
export function _setNavigateForTest(fn: (href: string) => void): void {
  navigate = fn;
}

function onRow(n: Notification): void {
  void NOTIF.markRead(n.id);
  navigate(notifHref(n));
  close();
}

function rowView(n: Notification) {
  return html`<button
    class="notifrow ${n.read_at ? '' : 'unread'}"
    role="menuitem"
    data-nid=${n.id}
    @click=${() => onRow(n)}
  >
    <span class="notifkind k-${n.kind}">${KIND_LABEL[n.kind] || n.kind}</span>
    <span class="notifbody">${n.body || KIND_LABEL[n.kind] || n.kind}</span>
    <span class="notifmeta">${timeAgo(n.created_at)}</span>
  </button>`;
}

function panelView() {
  const items = NOTIF.items;
  return html`<div class="notifpanel" role="menu">
    <div class="notifhead">
      <span>Notifications</span>
      ${NOTIF.unreadCount > 0
        ? html`<button
            class="notiflink"
            @click=${(e: Event) => {
              e.stopPropagation();
              void NOTIF.markAllRead();
            }}
          >
            Mark all read
          </button>`
        : nothing}
    </div>
    <div class="notiflist">
      ${items.length
        ? items.map(rowView)
        : html`<div class="notifempty">You're all caught up.</div>`}
    </div>
  </div>`;
}

function view() {
  const unread = NOTIF.unreadCount;
  return html`<button
      class="notifbell ${open ? 'open' : ''}"
      title="Notifications"
      aria-label="Notifications${unread > 0 ? ` (${unread} unread)` : ''}"
      aria-expanded=${open ? 'true' : 'false'}
      @click=${toggle}
    >
      ${bellIcon}<span class="lbl">Notifications</span>
      ${unread > 0
        ? html`<span class="notifbadge">${unread > 99 ? '99+' : unread}</span>`
        : nothing}
    </button>
    ${open ? panelView() : nothing}`;
}

function paint(): void {
  if (host) render(view(), host);
}

function outside(e: MouseEvent): void {
  if (host && !host.contains(e.target as Node)) close();
}

function openPanel(): void {
  open = true;
  paint();
  void NOTIF.refresh();
  // deferred so THIS click (which opened the panel) doesn't immediately
  // bubble to the document handler and close it again
  setTimeout(() => document.addEventListener('click', outside));
}

function close(): void {
  if (!open) return;
  open = false;
  paint();
  document.removeEventListener('click', outside);
}

function toggle(e: Event): void {
  e.stopPropagation();
  if (open) close();
  else openPanel();
}

/** Mount the bell into `el`, subscribe it to the NOTIF model, and start the
 * feed poll. Idempotent-friendly: a second call re-subscribes safely. */
export function initNotifBell(el: HTMLElement): void {
  host = el;
  // a freshly mounted bell always starts with the panel closed and no stale
  // outside-click listener from a previous mount still attached to document
  open = false;
  document.removeEventListener('click', outside);
  if (unsub) unsub();
  unsub = NOTIF.subscribe(paint);
  paint();
  NOTIF.start();
}
